import config from 'constants/config'
import { initSentryClientSide, sendErrorToSentry, sendMessageToSentry } from '../lib/utils/sentryUtils'
import type { AppStore } from '../store'
import getReportDataFromState from 'reporting/utils/getReportDataFromState'

type SentryMessageLevel = 'fatal' | 'error' | 'warning' | 'log' | 'info' | 'debug'
type SentryMessageTags = Record<string, string>

let _store: AppStore | undefined

export function registerErrorReportStore(store: AppStore) {
  _store = store
}

let Sentry: any
export async function reportClientError(error: unknown, additional: Record<string, any> = {}) {
  const { account, meta } = getReportDataFromState(_store?.getState())

  const extras = { ...meta, ...additional }

  // Logged before the Sentry check, deliberately. `SENTRY_ID` is unset in local dev, so gating the
  // console on it meant every reported problem was invisible on the one machine where someone is
  // actively debugging — the local console is the whole point of these reports outside production.
  if (!config.APP_ENV || config.APP_ENV !== 'production') {
    console.warn('Client error reported:', error, { memberId: account?.memberId }, extras)
  }

  if (!config.SENTRY_ID) return Promise.resolve()

  if (!Sentry) {
    Sentry = await import('@sentry/browser')
    initSentryClientSide(Sentry)
  }

  return sendErrorToSentry(error, account, extras)
}

export async function reportClientMessage(
  message: string,
  additional: Record<string, any> = {},
  level: SentryMessageLevel = 'warning',
  tags: SentryMessageTags = {},
) {
  const { account, meta } = getReportDataFromState(_store?.getState())
  const extras = { ...meta, ...additional }

  // See `reportClientError`: the local console must not depend on Sentry being configured.
  if (!config.APP_ENV || config.APP_ENV !== 'production') {
    console.warn(`Client ${level} reported:`, message, { memberId: account?.memberId }, extras)
  }

  if (!config.SENTRY_ID) return Promise.resolve()

  if (!Sentry) {
    Sentry = await import('@sentry/browser')
    initSentryClientSide(Sentry)
  }

  return sendMessageToSentry(message, account, extras, level, tags)
}
